import { useEffect, useState } from 'react';

import useApi from '@hooks/useApi';

import { PlotListItem, PlotListItemsResponse } from '@models/Plot';

import Endpoints from '@services/Endpoints';

import Logger from '@util/Logger';

const logger = Logger.make('useExperimentAnalyses');

type Props = {
    experimentId?: string;
    skip?: boolean;
};

/**
 * Custom hook to fetch plot list items for an experiment
 */
const useExperimentPlotListItems = ({ experimentId, skip }: Props) => {
    const [error, setError] = useState<string | null>(null);
    const api = useApi();
    const [plotListItems, setPlotListItems] = useState<PlotListItem[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [plotListItemCount, setPlotListItemCount] = useState<number>(0);

    const fetchPlotList = async () => {
        if (!experimentId) return;
        setLoading(true);
        try {
            const newResponse = await api.get<PlotListItemsResponse>(
                Endpoints.lab.experiment.plotsV2({ experimentId }),
            );
            setPlotListItems(newResponse.items ?? []);
            setPlotListItemCount(newResponse.count ?? 0);
        } catch (error) {
            logger.log(error);
            setError(error as string);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!experimentId || skip) return;
        logger.debug('Fetching plot list items for experiment:', experimentId);
        fetchPlotList();
    }, [experimentId, skip]);

    return {
        error,
        fetchPlotList,
        loading,
        plotListItemCount,
        plotListItems,
    };
};

export default useExperimentPlotListItems;
